import {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
  lazy,
  Suspense,
} from "react";
import PropTypes from "prop-types";
import {
  Drawer,
  Box,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import { GroupsServices } from "../Groups.services";
import GROUPS_CONSTANTS, {
  GROUP_ACCESS_DRAWER_TAB_KEYS,
  GROUP_ACCESS_DRAWER_TABS,
} from "../GroupsConstant";
import DataConnectionsTabContent from "./DataConnectionsTabContent";
import PageAccessControlTabContent from "./PageAccessControlTabContent";
import {
  drawerPaperSx,
  drawerRootBoxSx,
  drawerTitleSx,
  drawerSplitLayoutSx,
  drawerTabsPaneSx,
  drawerTabsSx,
  drawerTabSx,
  drawerTabPanelSx,
} from "./GroupAccessDrawer.style";
const TAB_KEYS = GROUP_ACCESS_DRAWER_TAB_KEYS;
const DRAWER_TABS = GROUP_ACCESS_DRAWER_TABS;

const Toast = lazy(() => import("common/Components/Toast"));

function modulesFromApi(res) {
  if (res?.status !== 200) return null;
  const list = res?.data?.modules;
  if (!Array.isArray(list)) return [];
  return [...list];
}

function failureToast(description) {
  return {
    show: true,
    type: GROUPS_CONSTANTS.toast.TYPE_FAILURE,
    title: GROUPS_CONSTANTS.groups.TOAST_ERROR_TITLE,
    description:
      typeof description === "string" && description.trim()
        ? description
        : GROUPS_CONSTANTS.toast.MESSAGE_FAILURE,
  };
}
const GroupAccessDrawer = ({
  open,
  onClose,
  customerName,
  customerCode,
  modules: modulesProp,
  onDataConnectionsApply,
  dataConnectionsApplying = false,
  groups,
  onModulesUpdated,
  getAllGroups,
}) => {
  const groupsServices = useMemo(() => GroupsServices(), []);
  const [moduleRows, setModuleRows] = useState([]);
  const [patchInFlight, setPatchInFlight] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_KEYS.DATA_CONNECTIONS);
  const [toast, setToast] = useState({
    show: false,
    type: GROUPS_CONSTANTS.toast.TYPE_FAILURE,
    title: "",
    description: "",
  });
  const dismissToast = useCallback(() => {
    setToast((t) => ({ ...t, show: false }));
  }, []);

  useEffect(() => {
    if (!open) {
      setActiveTab(TAB_KEYS.DATA_CONNECTIONS);
    }
  }, [open]);

  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      dismissToast();
    }
    prevOpenRef.current = open;
  }, [open, dismissToast]);

  useEffect(() => {
    if (!open) return undefined;
    if (modulesProp !== undefined) {
      setModuleRows(Array.isArray(modulesProp) ? [...modulesProp] : []);
      return undefined;
    }
    setModuleRows([]);
    return undefined;
  }, [open, modulesProp]);

  const handleDrawerClose = () => {
    dismissToast();
    onClose();
  };

  const handleToastClose = () => {
    setToast((prev) => ({ ...prev, show: false }));
  };

  const handleToggle = async (moduleKey, enabled) => {
    const snapshot = moduleRows;
    setModuleRows((rows) =>
      rows.map((r) => (r.module === moduleKey ? { ...r, enabled } : r))
    );
    dismissToast();

    const usePatchApi = Boolean(customerCode);
    if (!usePatchApi) {
      return;
    }

    setPatchInFlight(true);

    const patchRes = await groupsServices.patchModuleAccess(moduleKey, {
      enabled,
    }, customerCode);

    const nextRows = modulesFromApi(patchRes);
    if (nextRows !== null) {
      setModuleRows(nextRows);
      onModulesUpdated(nextRows);
      getAllGroups();
      setPatchInFlight(false);
      return;
    }

    if (patchRes.status === 200 || patchRes.status === 204) {
      setPatchInFlight(false);
      return;
    }

    setModuleRows(snapshot);
    setToast(failureToast(patchRes.error));
    setPatchInFlight(false);
  };

  const drawerTitle =
    customerName?.trim() !== "" ? `Customer: ${customerName}` : "Customer";
  const switchesDisabled = patchInFlight;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleDrawerClose}
        ModalProps={{ keepMounted: false }}
        PaperProps={{ sx: drawerPaperSx }}
      >
        <Box sx={drawerRootBoxSx}>
          <Typography component="h2" sx={drawerTitleSx}>
            {drawerTitle}
          </Typography>
          <Box sx={drawerSplitLayoutSx}>
            <Box sx={drawerTabsPaneSx}>
              <Tabs
                orientation="vertical"
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                sx={drawerTabsSx}
              >
                {DRAWER_TABS?.map((tab) => (
                  <Tab
                    key={tab?.value}
                    value={tab?.value}
                    label={tab?.label}
                    sx={drawerTabSx}
                  />
                ))}
              </Tabs>
            </Box>

            <Box sx={drawerTabPanelSx}>
              {activeTab === TAB_KEYS.PAGE_ACCESS_CONTROL ? (
                <PageAccessControlTabContent
                  loadingModules={false}
                  moduleRows={moduleRows}
                  switchesDisabled={switchesDisabled}
                  onToggle={handleToggle}
                />
              ) : (
                <DataConnectionsTabContent
                  onApply={onDataConnectionsApply}
                  applying={dataConnectionsApplying}
                  groups={groups}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Drawer>

      <Suspense fallback={null}>
        <Toast
          type={toast.type}
          title={toast.title}
          description={toast.description}
          show={toast.show}
          onClose={handleToastClose}
          duration={GROUPS_CONSTANTS.toast.DURATION}
        />
      </Suspense>
    </>
  );
};

GroupAccessDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customerName: PropTypes.string,
  customerCode: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  modules: PropTypes.arrayOf(
    PropTypes.shape({
      module: PropTypes.string.isRequired,
      enabled: PropTypes.bool.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  onDataConnectionsApply: PropTypes.func,
  dataConnectionsApplying: PropTypes.bool,
  groups: PropTypes.array,
  onModulesUpdated: PropTypes.func,
  getAllGroups: PropTypes.func,
};

export default GroupAccessDrawer;
